import type { DocumentProfile, FaceObservations, ValidationReport } from '@photovalidator/core';
import { createAnalytics, deviceClass } from './analytics/events.js';
import { CameraController } from './camera/cameraController.js';
import { createDebugPanel } from './debug/debugPanel.js';
import { decodeBitmap, ImageRejectedError } from './image/bitmap.js';
import { getDocumentRegistry, resolveDocument } from './profiles/loadProfiles.js';
import { createResultPanel } from './results/resultPanel.js';
import { PhotoStage } from './stage/photoStage.js';
import { createStore } from './state/store.js';
import { INITIAL_STATE, type AppState, type SourceImage } from './state/types.js';
import { checkBrowserSupport, userFacingError } from './ui/capabilities.js';
import { el } from './ui/dom.js';
import { createOverlayToggles } from './ui/overlayToggles.js';
import {
  createErrorPanel,
  createProgressPanel,
  createUnsupportedPanel,
  createUploadPanel,
} from './upload/uploadPanel.js';
import { createValidatorClient } from './validator-client/client.js';

/**
 * Application wiring.
 *
 * Data flow, one direction only:
 *
 *   file / camera → pipeline (worker) → validator-core → report
 *                                                          ↓
 *                                             overlay + result panel
 *
 * This is a **validator, not an editor**: the photo on the stage is the file the
 * user selected, unchanged. The interface only draws the diagnostic overlay
 * (centre axis, eye line, head zone, face contour) and lists what the core
 * measured — it never zooms, rotates, crops or "fixes" the picture.
 */

export interface AppOptions {
  mount: HTMLElement;
  initialDocumentId?: string;
  /** Mounted inside an SEO page that already provides the H1 and the text. */
  inline?: boolean;
  debug?: boolean;
}

export interface App {
  destroy(): void;
}

const cameraAvailable = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';

const unique = (values: readonly string[]): string[] => [...new Set(values.filter(Boolean))];

export const createApp = (options: AppOptions): App => {
  const registry = getDocumentRegistry();
  const analytics = createAnalytics();
  const support = checkBrowserSupport();

  if (!support.supported) {
    options.mount.replaceChildren(
      el('div', { class: 'pv-app' }, [
        el('header', { class: 'pv-header' }, [
          el('div', { class: 'pv-header__titles' }, [
            options.inline
              ? el('h2', { class: 'pv-panel__title', text: 'Проверка фотографии' })
              : el('h1', { text: 'Photo Validator — проверка фотографии на документы' }),
          ]),
        ]),
        createUnsupportedPanel(support),
      ]),
    );
    return {
      destroy: () => options.mount.replaceChildren(),
    };
  }

  const client = createValidatorClient();

  const initialId =
    (options.initialDocumentId && resolveDocument(options.initialDocumentId)?.id) ||
    registry.documents.documents[0]?.id ||
    '';

  const store = createStore<AppState>({
    ...INITIAL_STATE,
    documents: registry.documents.documents,
    documentId: initialId,
  });

  let camera: CameraController | null = null;

  const profileOf = (documentId: string): DocumentProfile | undefined =>
    registry.validator.getDocument(documentId);

  const summaryOf = (documentId: string) =>
    store.get().documents.find((document) => document.id === documentId);

  // ------------------------------------------------------------------ header

  const documentSelect = el('select', { id: 'pv-document-select', 'aria-label': 'Документ' });
  for (const document of registry.documents.documents) {
    documentSelect.append(el('option', { value: document.id, text: document.name }));
  }
  documentSelect.addEventListener('change', () => void selectDocument(documentSelect.value));

  // The photo panel keeps its two overlay checkboxes; "another photo" lives in
  // the header next to the document selector, so the result column carries only
  // the verdict, the groups and the single call to action.
  const checkAnotherButton = el('button', {
    class: 'pv-button pv-button--ghost pv-button--compact',
    type: 'button',
    text: 'Другое фото',
    'data-action': 'check-another',
    'data-role': 'check-another',
  });
  checkAnotherButton.addEventListener('click', () => checkAnother());
  checkAnotherButton.disabled = true;

  const header = el('header', { class: 'pv-header' }, [
    el('div', { class: 'pv-header__titles' }, [
      options.inline
        ? el('h2', { class: 'pv-panel__title', text: 'Проверить фотографию' })
        : el('h1', { text: 'Photo Validator — проверка фотографии на документы' }),
      el('p', {
        class: 'pv-header__subtitle',
        text: 'Загрузите фото — валидатор покажет прямо на снимке, где голова, глаза и кадр выходят за допустимые границы. Фотография не изменяется, обработка происходит в браузере.',
      }),
    ]),
    el('div', { class: 'pv-header__actions' }, [
      el('div', { class: 'pv-document-picker' }, [
        el('label', { for: 'pv-document-select', text: 'Документ' }),
        documentSelect,
      ]),
      checkAnotherButton,
    ]),
  ]);

  // ------------------------------------------------------------------- stage

  const stage = new PhotoStage({
    onSelectMeasurement: (measurementId) => {
      if (!measurementId) return;
      store.set({ highlightId: measurementId });
      const item = sideHost.querySelector<HTMLElement>(`[data-check-id="${measurementId}"]`);
      item?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
  });

  const overlayToggles = createOverlayToggles({
    onToggleOverlay: (value) => store.set({ showOverlay: value }),
    onToggleDetail: (value) => store.set({ detailMode: value }),
  });

  const stagePanel = el('section', { class: 'pv-panel' }, [
    el('div', { class: 'pv-row pv-row--between' }, [
      el('h2', { class: 'pv-panel__title', text: 'Фотография и разметка' }),
      el('span', {
        class: 'pv-small pv-muted',
        text: 'Показана ваша фотография без изменений.',
      }),
    ]),
    stage.element,
    overlayToggles.element,
  ]);

  // ------------------------------------------------------------ hosts / layout

  const sideHost = el('div');
  const uploadHost = el('div');
  /** Progress of the expensive stages, shown while the photo is already visible. */
  const progressHost = el('div', { class: 'pv-progress-host' });
  const cameraHost = el('div');
  const debugHost = el('div');

  const root = el('div', { class: 'pv-app' }, [
    header,
    el('div', { class: 'pv-layout' }, [
      el('div', { class: 'pv-layout__main' }, [stagePanel, progressHost, cameraHost, uploadHost]),
      el('div', { class: 'pv-layout__side' }, [sideHost, debugHost]),
    ]),
  ]);
  options.mount.replaceChildren(root);

  // ------------------------------------------------------------- render logic

  const renderSide = (): void => {
    const state = store.get();

    // The panel is rebuilt from scratch on every new report, so no accordion
    // state survives between reports: all groups start closed.
    sideHost.replaceChildren(
      ...(state.error ? [createErrorPanel(state.error)] : []),
      // ONE friendly banner for every missing capability; its own module keeps
      // the raw reasons out of the default view — they live in `?debug=1`.
      createResultPanel(state.report, {
        capabilityIssues: state.capabilityIssues,
        // Diagnostics only: clicking a row highlights that measurement on the
        // photo. Nothing is moved, cropped, rotated or re-encoded.
        onSelect: (checkId) => {
          store.set({ highlightId: checkId });
          stage.focusMeasurement(checkId);
        },
        onCtaClick: () =>
          analytics.track('cta_clicked', { document: state.documentId, device: deviceClass() }),
      }),
    );
  };

  const renderUpload = (): void => {
    const state = store.get();
    const summary = summaryOf(state.documentId);
    const profile = profileOf(state.documentId);

    if (state.image) {
      uploadHost.replaceChildren();
      return;
    }
    if (state.phase === 'decoding' || state.phase === 'analysing') {
      uploadHost.replaceChildren(
        createProgressPanel(
          state.phase === 'decoding' ? 'Декодируем фотографию…' : 'Анализируем лицо и качество…',
        ),
      );
      return;
    }
    const digital = profile?.image.digital;
    uploadHost.replaceChildren(
      createUploadPanel(
        { onFile: (file) => void handleFile(file, 'file'), onCamera: () => void openCamera() },
        {
          documentName: summary?.name ?? 'документ',
          formats: profile?.image.formats ?? ['jpeg'],
          maxBytes: profile?.image.maxFileSize ?? 5_242_880,
          minWidth: digital?.minWidth ?? profile?.image.minWidth ?? 600,
          minHeight: digital?.minHeight ?? profile?.image.minHeight ?? 600,
          maxWidth: digital?.maxWidth ?? profile?.image.maxWidth ?? 1200,
          maxHeight: digital?.maxHeight ?? profile?.image.maxHeight ?? 1200,
          cameraAvailable: cameraAvailable(),
          busy: state.busyLabel !== undefined,
          ...(state.busyLabel ? { busyLabel: state.busyLabel } : {}),
        },
      ),
    );
  };

  const renderDebug = (): void => {
    const state = store.get();
    debugHost.replaceChildren(
      options.debug
        ? createDebugPanel(state.report, {
            documentId: state.documentId,
            phase: state.phase,
            showOverlay: state.showOverlay,
            detailMode: state.detailMode,
            overlayNodes: stage.element.querySelectorAll('.pv-stage__overlay > *').length,
            quickVerdict: state.quickReport?.overall.verdict ?? null,
            capabilityIssues: state.capabilityIssues,
            browser: support,
            lastError: state.debugError ?? null,
            pipeline: state.preprocessed
              ? {
                  detector: state.preprocessed.faceModel,
                  decodeMs: state.preprocessed.decodeMs,
                  analyseMs: state.preprocessed.analyseMs,
                  landmarks: state.preprocessed.faces.faces.map((face) => face.landmarks.length),
                  faceModelReason: state.preprocessed.faceModelReason ?? null,
                }
              : null,
          })
        : el('span'),
    );
  };

  let stageKey = '';
  const renderStage = (): void => {
    const state = store.get();
    const key = [
      state.report?.reportId ?? 'none',
      state.showOverlay ? 'overlay' : 'clean',
      state.detailMode ? 'detail' : 'plain',
      state.highlightId ?? '',
    ].join('|');
    if (key !== stageKey) {
      stageKey = key;
      stage.applyState({
        report: state.report,
        showOverlay: state.showOverlay,
        detailMode: state.detailMode,
        highlightId: state.highlightId,
      });
    }
    overlayToggles.update({
      showOverlay: state.showOverlay,
      detailMode: state.detailMode,
      hasImage: Boolean(state.image),
    });
    checkAnotherButton.disabled = !state.image;
    // The advanced stage can take seconds: say so instead of freezing silently.
    progressHost.replaceChildren(
      ...(state.image && state.busyLabel ? [createProgressPanel(state.busyLabel)] : []),
    );
    // Public "is the validator still working" signal for the UI and the E2E
    // suite: the deep analysis pass is over when it flips back to `false`.
    root.dataset['validating'] = state.busyLabel ? 'true' : 'false';
    if (documentSelect.value !== state.documentId) documentSelect.value = state.documentId;
  };

  let sideKey = '';
  const render = (): void => {
    const state = store.get();
    renderStage();
    renderUpload();
    const key = [
      state.report?.reportId ?? 'none',
      state.documentId,
      state.error ?? '',
      state.busyLabel ?? '',
      state.capabilityIssues.join('|'),
    ].join('|');
    if (key !== sideKey) {
      sideKey = key;
      renderSide();
    }
    renderDebug();
  };

  const unsubscribe = store.subscribe(render);

  // ----------------------------------------------------------------- handlers

  const trackReport = (report: ValidationReport): void => {
    const base = {
      document: report.documentId,
      device: deviceClass(),
      verdict: report.overall.verdict,
      processingMs: report.processingMs,
      checks: report.checks.length,
    };
    analytics.track('validation_completed', base);
    if (report.overall.verdict === 'pass') analytics.track('validation_pass', base);
    else if (report.overall.verdict === 'fail') analytics.track('validation_fail', base);
    else analytics.track('validation_uncertain', base);
    for (const check of report.checks) {
      if (check.status === 'fail' || check.status === 'warning') {
        analytics.track('failed_rule', {
          document: report.documentId,
          rule: check.id,
          status: check.status,
        });
      }
    }
  };

  const capabilityIssuesOf = (
    report: ValidationReport,
    preprocessed: AppState['preprocessed'],
  ): string[] => {
    const issues = [...report.engine.capabilityIssues];
    // The warmup status carries the human reason for the model failure even
    // when the report itself only says "недоступно".
    if (preprocessed?.faceModel === 'unavailable' && preprocessed.faceModelReason) {
      issues.push(`Модель распознавания лиц: ${preprocessed.faceModelReason}`);
    }
    return unique(issues);
  };

  const runValidation = async (): Promise<void> => {
    const state = store.get();
    const profile = profileOf(state.documentId);
    if (!profile || !state.image) return;
    analytics.track('validation_started', { document: profile.id, device: deviceClass() });
    store.set({ busyLabel: 'Анализируем фотографию…', error: undefined, debugError: undefined });

    // Three stages, each one shown as soon as it lands:
    //   quick    — technical + geometry (the answer to "is the frame usable?")
    //   deep     — + quality and background
    //   advanced — + the authenticity ensemble, which is the expensive part
    const quick = await client.validate(profile.id, profile, true, 'quick');
    store.set({ quickReport: quick, report: quick, phase: 'quick' });

    const deep = await client.validate(profile.id, profile, false, 'deep');
    store.set({ report: deep, phase: 'deep', busyLabel: 'Расширенная проверка изображения…' });

    const advanced = await client.validate(profile.id, profile, false, 'advanced');
    store.set({
      report: advanced,
      phase: 'advanced',
      busyLabel: undefined,
      capabilityIssues: capabilityIssuesOf(advanced, store.get().preprocessed),
    });
    trackReport(advanced);
  };

  const selectDocument = async (idOrAlias: string): Promise<void> => {
    const profile = resolveDocument(idOrAlias);
    if (!profile) {
      store.set({ error: `Документ «${idOrAlias}» не найден.` });
      return;
    }
    store.set({ documentId: profile.id, error: undefined });
    analytics.track('document_selected', { document: profile.id });
    if (profile.seo?.slug && !options.inline) {
      const url = new URL(window.location.href);
      url.pathname = `/validator/${profile.seo.slug}`;
      window.history.replaceState({}, '', url);
      if (profile.seo.title) document.title = profile.seo.title;
    }
    // Same photo, new profile: no re-decode, no re-detection.
    if (store.get().image) await runValidation();
  };

  const acceptSource = (
    bitmap: ImageBitmap,
    source: {
      image: { width: number; height: number; detectedMimeType?: string };
      faces: FaceObservations;
      decodeMs: number;
      analyseMs: number;
      faceModel: 'ready' | 'unavailable';
      faceModelReason?: string;
    },
    extra: {
      sourceKind: SourceImage['sourceKind'];
      fileName?: string;
      declaredMimeType?: string;
      sizeBytes?: number;
    },
  ): void => {
    store.get().image?.bitmap.close();
    stage.setImage(bitmap);

    const image: SourceImage = {
      bitmap,
      width: bitmap.width,
      height: bitmap.height,
      sourceKind: extra.sourceKind,
      ...(extra.fileName ? { fileName: extra.fileName } : {}),
      ...(extra.sizeBytes !== undefined ? { sizeBytes: extra.sizeBytes } : {}),
      ...(source.image.detectedMimeType ? { detectedMimeType: source.image.detectedMimeType } : {}),
      ...(extra.declaredMimeType ? { declaredMimeType: extra.declaredMimeType } : {}),
    };

    store.set({
      image,
      preprocessed: {
        faces: source.faces,
        decodeMs: source.decodeMs,
        analyseMs: source.analyseMs,
        faceModel: source.faceModel,
        ...(source.faceModelReason ? { faceModelReason: source.faceModelReason } : {}),
      },
      phase: 'analysing',
      capabilityIssues: [],
    });
  };

  const handleFile = async (
    file: File | Blob,
    sourceKind: SourceImage['sourceKind'],
  ): Promise<void> => {
    const profile = profileOf(store.get().documentId);
    if (!profile) return;
    store.set({ phase: 'decoding', busyLabel: 'Декодируем фотографию…', error: undefined });

    try {
      const decoded =
        file instanceof File ? file : new File([file], 'camera.jpg', { type: 'image/jpeg' });
      const source = await client.setSource(decoded, {
        sourceKind,
        fileName: decoded.name,
        declaredMimeType: decoded.type,
        sizeBytes: decoded.size,
      });
      const bitmap = await decodeBitmap(decoded);
      acceptSource(
        bitmap,
        {
          image: source.image,
          faces: source.faces,
          decodeMs: source.decodeMs,
          analyseMs: source.analyseMs,
          faceModel: source.faceModel,
          ...(source.faceModelReason ? { faceModelReason: source.faceModelReason } : {}),
        },
        {
          sourceKind,
          fileName: decoded.name,
          declaredMimeType: decoded.type,
          sizeBytes: decoded.size,
        },
      );
      analytics.track('photo_uploaded', {
        document: profile.id,
        device: deviceClass(),
        faceModel: source.faceModel,
        source: sourceKind,
      });
      await runValidation();
    } catch (error) {
      // Our own rejections are already written for humans; everything else gets
      // a generic explanation and keeps the technical text for the debug panel.
      const { message, technical } = userFacingError(error, {
        known: error instanceof ImageRejectedError,
      });
      store.set({
        phase: 'error',
        error: message,
        debugError: technical,
        busyLabel: undefined,
        image: undefined,
        capabilityIssues: [],
      });
    }
  };

  const checkAnother = (): void => {
    camera?.dispose();
    camera = null;
    cameraHost.replaceChildren();
    store.get().image?.bitmap.close();
    stage.clear();
    store.set({
      phase: 'empty',
      image: undefined,
      preprocessed: undefined,
      report: undefined,
      quickReport: undefined,
      error: undefined,
      highlightId: undefined,
      busyLabel: undefined,
      debugError: undefined,
      capabilityIssues: [],
    });
  };

  const openCamera = async (): Promise<void> => {
    const profile = profileOf(store.get().documentId);
    if (!profile) return;
    analytics.track('camera_opened', { document: profile.id, device: deviceClass() });
    camera?.dispose();
    camera = new CameraController({
      profile,
      stage,
      client,
      onError: (message) => store.set({ error: message }),
      onCapture: (blob) => {
        camera?.dispose();
        camera = null;
        cameraHost.replaceChildren();
        void handleFile(
          new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }),
          'camera',
        );
      },
    });
    cameraHost.replaceChildren(camera.element);
    await camera.start();
  };

  // ------------------------------------------------------------------- start

  analytics.track('validator_open', { document: initialId, device: deviceClass() });
  render();

  return {
    destroy: () => {
      unsubscribe();
      camera?.dispose();
      stage.destroy();
      client.dispose();
      store.get().image?.bitmap.close();
      options.mount.replaceChildren();
    },
  };
};
